/**
 * Проекты: один список на всё приложение — хаб рисует из него карточки,
 * сайдбар свои пункты. Два независимых запроса за одним и тем же означали бы
 * два разных списка на экране, что читается как ошибка прав.
 *
 * Фильтр и поиск уходят на сервер: список проектов у человека короткий, но
 * фильтровать его на клиенте значит показывать не то, что показал бы бэкенд,
 * когда проектов станет много.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useNavigationStore } from '@/stores/navigation.store'

import {
  createProject,
  listProjects,
  setProjectArchived,
  type CreateProjectPayload,
  type Project,
  type ProjectMember,
} from './api'

export type ProjectFilter = 'all' | 'mine' | 'archived'

export const useProjectsStore = defineStore('projects', () => {
  const navigation = useNavigationStore()

  const items = ref<Project[]>([])
  /** Участники по id проекта — для аватаров на карточках */
  const members = ref<Record<string, ProjectMember[]>>({})
  const isLoading = ref(false)
  const error = ref<unknown>(null)
  const isLoaded = ref(false)

  const openIssueTotal = computed(() =>
    items.value.reduce((total, project) => total + project.openIssueCount, 0),
  )

  const myProjectCount = computed(
    () => items.value.filter((project) => project.myRole && project.myRole !== 'MEMBER_ROLE_INVALID').length,
  )

  const load = async (params: { filter?: ProjectFilter; search?: string } = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const next = await listProjects({
        onlyMine: params.filter === 'mine' || undefined,
        archived: params.filter === 'archived' || undefined,
        search: params.search,
      })

      items.value = next
      isLoaded.value = true

      // Аватары приезжают вместе со списком (DEV-1500): раньше их спрашивали
      // по проекту, и хаб на десяти проектах делал одиннадцать запросов.
      members.value = Object.fromEntries(next.map((project) => [project.id, project.members]))

      // Сайдбар показывает живые проекты вошедшего, а не результат фильтра
      // экрана: иначе поиск по хабу вычищал бы навигацию, а вкладка «Архив»
      // подменяла бы её архивными.
      if (!params.filter && !params.search) {
        navigation.setProjects(
          next.map((project) => ({
            key: project.key,
            name: project.name,
            color: project.color,
            openCount: project.openIssueCount,
          })),
        )
        navigation.counters = { ...navigation.counters, projects: next.length }
      }

    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /** Для сайдбара: список нужен на любом экране, но грузить его стоит один раз. */
  const ensureLoaded = async () => {
    if (isLoaded.value || isLoading.value) return

    await load()
  }

  const create = async (payload: CreateProjectPayload): Promise<Project> => {
    const project = await createProject(payload)

    await load()

    return project
  }

  /** Архивировать или вернуть проект: список после этого перечитывается. */
  const setArchived = async (id: string, archived: boolean) => {
    await setProjectArchived(id, archived)
    await load()
  }

  return {
    items,
    members,
    isLoading,
    error,
    isLoaded,
    openIssueTotal,
    myProjectCount,
    load,
    ensureLoaded,
    create,
    setArchived,
  }
})
